if (!customElements.get('shopify-spin-viewer')) {
  customElements.define(
    'shopify-spin-viewer',
    class ShopifySpinViewer extends HTMLElement {
      constructor() {
        super();
        this.frames = [];
        this.frameIndex = 0;
        this.dragStartX = null;
        this.dragStartFrame = 0;
        this.playTimer = null;
        this.teaserDelayTimer = null;
        this.teaserInterval = null;
        this.teaserStep = 0;
        this.teaserShown = false;
        this.teaserRunId = 0;
        this.pendingAutospinVariantId = null;
        this.variantSelectionTimer = null;
        this.variantFallbackTimer = null;
        this.preloaded = new Set();
        // Retain a bounded cache across Shopify section/variant replacements.
        window.shopifySpinFrameCache ||= { loads: new Map(), decoded: new Map() };
        this.sourceLoads = window.shopifySpinFrameCache.loads;
        this.decodedSources = window.shopifySpinFrameCache.decoded;
        this.prewarmFrames = null;
        this.onMotionIntent = (event) => this.prepareMotionIntent(event.target);
        this.motionRunId = 0;
        this.motionRaf = null;
        this.motionTarget = null;
        this.onPreviousClick = () => this.step(-1);
        this.onNextClick = () => this.step(1);
        this.onPlayClick = () => this.togglePlay();
        this.onPointerDownBound = this.onPointerDown.bind(this);
        this.onPointerMoveBound = this.onPointerMove.bind(this);
        this.onPointerUpBound = this.onPointerUp.bind(this);
        this.onKeyDownBound = this.onKeyDown.bind(this);
        this.onVariantPickerInteraction = (event) => {
          const picker = event.target.closest('variant-selects');
          if (picker && this.productRoot?.contains(picker)) this.showSpin();
        };
      }

      connectedCallback() {
        this.image = this.querySelector('[data-spin-image]');
        this.stage = this.querySelector('[data-spin-stage]');
        this.status = this.querySelector('[data-spin-status]');
        this.playButton = this.querySelector('[data-spin-play]');
        this.controls = this.querySelector('[data-spin-controls]');
        this.supportStages = [...this.querySelectorAll('[data-spin-support]')];
        this.slide = this.closest('[data-media-id]');
        this.gallery = this.closest('media-gallery');
        this.productRoot = this.closest('product-info') || this.closest('.product');

        try {
          this.spinMap = JSON.parse(this.querySelector('[data-spin-map]')?.textContent || '{}');
          Object.keys(this.spinMap).forEach(id => { if (typeof this.spinMap[id] === 'string') this.spinMap[id] = this.spinMap[this.spinMap[id]] || []; });
          this.fallbackMap = JSON.parse(this.querySelector('[data-spin-fallback-map]')?.textContent || '{}');
          this.mediaMap = JSON.parse(this.querySelector('[data-spin-media-map]')?.textContent || '{}');
          this.optionMap = JSON.parse(this.querySelector('[data-spin-option-map]')?.textContent || '{}');
        } catch (error) {
          console.error('Kon 360°-gegevens niet lezen.', error);
          this.spinMap = {};
          this.fallbackMap = {};
          this.mediaMap = {};
          this.optionMap = {};
        }

        this.querySelector('[data-spin-previous]')?.addEventListener('click', this.onPreviousClick);
        this.querySelector('[data-spin-next]')?.addEventListener('click', this.onNextClick);
        this.playButton?.addEventListener('click', this.onPlayClick);
        this.stage?.addEventListener('pointerdown', this.onPointerDownBound);
        this.stage?.addEventListener('pointermove', this.onPointerMoveBound);
        this.stage?.addEventListener('pointerup', this.onPointerUpBound);
        this.stage?.addEventListener('pointercancel', this.onPointerUpBound);
        this.stage?.addEventListener('keydown', this.onKeyDownBound);
        this.productRoot?.addEventListener('click', this.onVariantPickerInteraction, true);
        this.productRoot?.addEventListener('change', this.onVariantPickerInteraction, true);
        ['pointerdown', 'change'].forEach(type => {
          this.productRoot?.addEventListener(type, this.onMotionIntent, true);
        });

        this.onSpinThumbnailClick = (event) => {
          const supportThumbnail = event.target.closest('[data-spin-support-thumbnail]');
          if (supportThumbnail && this.contains(supportThumbnail)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.showSupport(supportThumbnail.dataset.spinSupportThumbnail);
            return;
          }

          const currentThumbnail = event.target.closest('[data-spin-current-thumbnail]');
          if (currentThumbnail && this.contains(currentThumbnail)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.showSpin();
            this.showFrame(0);
            return;
          }

          const thumbnail = event.target.closest('[data-spin-variant-thumbnail]');
          if (thumbnail && (this.contains(thumbnail) || this.gallery?.contains(thumbnail))) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.selectVariantFromThumbnail(thumbnail);
            return;
          }

          const mediaThumbnail = event.target.closest('[data-target]');
          if (!mediaThumbnail || !this.gallery?.contains(mediaThumbnail)) return;
          const mediaTarget = mediaThumbnail.dataset.target;
          if (!mediaTarget || typeof this.gallery.setActiveMedia !== 'function') return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.cancelMotion();
          this.stopTeaser();
          this.stop();
          this.gallery.setActiveMedia(mediaTarget, false);
        };
        this.gallery?.addEventListener('click', this.onSpinThumbnailClick, true);
        this.addEventListener('click', this.onSpinThumbnailClick, true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.variant?.id && String(data.variant.id) !== this.currentVariantId) this.setVariant(data.variant.id);
          });
        }

        this.setVariant(this.dataset.initialVariant);
      }

      disconnectedCallback() {
        this.cancelMotion();
        this.stopTeaser();
        this.stop();
        if (this.variantSelectionTimer) window.clearTimeout(this.variantSelectionTimer);
        if (this.variantFallbackTimer) window.clearTimeout(this.variantFallbackTimer);
        this.querySelector('[data-spin-previous]')?.removeEventListener('click', this.onPreviousClick);
        this.querySelector('[data-spin-next]')?.removeEventListener('click', this.onNextClick);
        this.playButton?.removeEventListener('click', this.onPlayClick);
        this.stage?.removeEventListener('pointerdown', this.onPointerDownBound);
        this.stage?.removeEventListener('pointermove', this.onPointerMoveBound);
        this.stage?.removeEventListener('pointerup', this.onPointerUpBound);
        this.stage?.removeEventListener('pointercancel', this.onPointerUpBound);
        this.stage?.removeEventListener('keydown', this.onKeyDownBound);
        this.productRoot?.removeEventListener('click', this.onVariantPickerInteraction, true);
        this.productRoot?.removeEventListener('change', this.onVariantPickerInteraction, true);
        ['pointerdown', 'change'].forEach(type => {
          this.productRoot?.removeEventListener(type, this.onMotionIntent, true);
        });
        this.gallery?.removeEventListener('click', this.onSpinThumbnailClick, true);
        this.removeEventListener('click', this.onSpinThumbnailClick, true);
        this.unsubscribeVariant?.();
      }

      selectVariantFromThumbnail(thumbnail) {
        this.cancelMotion();
        let variantId = thumbnail.dataset.spinVariantThumbnail;
        if (!variantId) return;

        this.stopTeaser();
        this.stop();
        this.showSpin();
        if (this.variantSelectionTimer) window.clearTimeout(this.variantSelectionTimer);
        if (this.variantFallbackTimer) window.clearTimeout(this.variantFallbackTimer);

        let optionValues = [];
        try {
          optionValues = JSON.parse(thumbnail.dataset.spinOptions || '[]');
        } catch (error) {
          optionValues = [];
        }

        const mediaId = thumbnail.dataset.spinMediaId;
        if (mediaId) {
          const currentOptions = this.optionMap?.[this.currentVariantId] || [];
          const candidates = Object.entries(this.mediaMap || {})
            .filter(([candidateId, candidateMediaId]) => (
              String(candidateMediaId) === String(mediaId) && (this.spinMap?.[candidateId] || []).length > 1
            ))
            .map(([candidateId]) => ({
              id: candidateId,
              options: this.optionMap?.[candidateId] || [],
            }))
            .sort((left, right) => {
              const score = (candidate) => candidate.options.reduce(
                (total, value, index) => total + (value === currentOptions[index] ? 1 : 0),
                0,
              );
              return score(right) - score(left) || String(left.id).localeCompare(String(right.id));
            });
          if (candidates.length) {
            variantId = candidates[0].id;
            optionValues = candidates[0].options;
          }
        }

        this.variantSelectionTimer = window.setTimeout(() => {
          this.variantSelectionTimer = null;
          this.commitVariantSelection(String(variantId), optionValues);
        }, 180);
      }

      commitVariantSelection(variantId, optionValues) {
        const previousVariantId = this.currentVariantId;
        if (previousVariantId === variantId) {
          this.showSpin();
          this.showFrame(0);
          this.scheduleTeaser(true);
          return;
        }

        this.pendingAutospinVariantId = variantId;

        const picker = this.closest('.product')?.querySelector('variant-selects');
        const controls = picker ? [...picker.querySelectorAll('fieldset, select')] : [];
        let selectionChanged = false;

        optionValues.forEach((value, index) => {
          const control = controls[index];
          if (!control) return;

          if (control.matches('select')) {
            if (control.value !== value) {
              control.value = value;
              control.dispatchEvent(new Event('change', { bubbles: true }));
              selectionChanged = true;
            }
            return;
          }

          const radio = [...control.querySelectorAll('input[type="radio"]')].find((input) => input.value === value);
          if (radio && !radio.checked) {
            radio.click();
            selectionChanged = true;
          }
        });

        if (!selectionChanged && previousVariantId !== variantId) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          window.location.assign(url.toString());
          return;
        }

        this.variantFallbackTimer = window.setTimeout(() => {
          const activeVariantId = new URL(window.location.href).searchParams.get('variant');
          if (activeVariantId === variantId) return;
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          window.location.assign(url.toString());
        }, 2500);
      }

      setVariant(variantId) {
        this.cancelMotion();
        const normalizedVariantId = String(variantId);
        const autospinAfterChange = this.pendingAutospinVariantId === normalizedVariantId;
        if (autospinAfterChange) {
          this.pendingAutospinVariantId = null;
          if (this.variantFallbackTimer) window.clearTimeout(this.variantFallbackTimer);
          this.variantFallbackTimer = null;
        }
        this.stopTeaser();
        this.stop();
        this.currentVariantId = normalizedVariantId;
        this.frames = this.spinMap[normalizedVariantId] || [];
        // Keep the last color available for instant return; evict by budget,
        // not on every selection. Mark this series as recently used.
        this.frames.forEach(frame => {
          const src = frame.teaser || frame.src;
          if (this.decodedSources.has(src)) {
            const image = this.decodedSources.get(src);
            this.decodedSources.delete(src); this.decodedSources.set(src, image);
          }
        });
        this.showSpin();
        this.updateVariantMedia(normalizedVariantId);
        this.frameIndex = 0;
        const available = this.frames.length > 1;
        const thumbnail = this.gallery?.querySelector(`[data-target="${this.slide?.dataset.mediaId}"]`);
        const thumbnailImage = thumbnail?.querySelector('.shopify-spin-thumbnail__image');
        const currentThumbnail = this.querySelector('[data-spin-current-thumbnail]');
        const currentThumbnailImage = currentThumbnail?.querySelector('img');

        if (this.slide) this.slide.hidden = !available;
        if (thumbnail) thumbnail.hidden = !available;
        if (thumbnailImage && available) {
          thumbnailImage.src = this.frames[0].thumb || this.frames[0].src;
          thumbnailImage.alt = this.frames[0].alt || '';
        }
        if (currentThumbnail) currentThumbnail.hidden = !available;
        if (currentThumbnailImage && available) {
          currentThumbnailImage.src = this.frames[0].thumb || this.frames[0].src;
          currentThumbnailImage.alt = this.frames[0].alt || '';
        }
        this.classList.toggle('shopify-spin--available', available);
        this.querySelectorAll('[data-spin-variant-thumbnail]').forEach((thumbnail) => {
          const isActive = thumbnail.dataset.spinVariantThumbnail === String(variantId);
          thumbnail.classList.toggle('is-active', isActive);
          if (isActive) thumbnail.setAttribute('aria-current', 'true');
          else thumbnail.removeAttribute('aria-current');
        });

        if (!available) {
          // A static-only variant must keep its native first photo visible.
          const mediaId = this.mediaMap?.[normalizedVariantId];
          const staticSlide = mediaId && this.gallery?.querySelector(`[data-media-id$="-${mediaId}"]`);
          if (staticSlide) {
            staticSlide.hidden = false;
            this.gallery?.setActiveMedia?.(staticSlide.dataset.mediaId, false);
          }
          const fallback = this.fallbackMap[normalizedVariantId];
          if (this.controls) this.controls.hidden = true;
          if (fallback?.src && this.image) {
            this.image.src = fallback.src;
            this.image.alt = fallback.alt || '';
          } else {
            this.image?.removeAttribute('src');
          }
          if (this.status) this.status.textContent = window.LCS?.t('Statische productfoto') || 'Statische productfoto';
          return;
        }

        this.showFrame(0);
        // Keep the first image stable during loading. Motion starts on explicit interaction.
        if (autospinAfterChange) {
          this.scheduleTeaser(true);
          this.warmMotionFrames();
        }
      }

      showSpin() {
        if (this.stage) this.stage.hidden = false;
        if (this.controls) this.controls.hidden = this.frames.length <= 1;
        // The viewer lives in a native gallery slide. Revealing its contents alone
        // cannot return from a supporting photo, especially for the same variant
        // (which does not trigger setVariant/updateVariantMedia).
        if (this.frames.length > 1 && this.slide && !this.slide.classList.contains('is-active')) {
          this.slide.hidden = false;
          this.gallery?.setActiveMedia?.(this.slide.dataset.mediaId, false);
        }
        this.supportStages?.forEach((stage) => { stage.hidden = true; });
        const currentThumbnail = this.querySelector('[data-spin-current-thumbnail]');
        if (currentThumbnail) {
          currentThumbnail.classList.add('is-active');
          currentThumbnail.setAttribute('aria-current', 'true');
        }
        this.querySelectorAll('[data-spin-variant-thumbnail]').forEach((thumbnail) => {
          const isActive = thumbnail.dataset.spinVariantThumbnail === this.currentVariantId;
          thumbnail.classList.toggle('is-active', isActive);
          if (isActive) thumbnail.setAttribute('aria-current', 'true');
          else thumbnail.removeAttribute('aria-current');
        });
        this.querySelectorAll('[data-spin-support-thumbnail]').forEach((thumbnail) => {
          thumbnail.classList.remove('is-active');
          thumbnail.removeAttribute('aria-current');
        });
        if (this.status && this.frames.length) {
          this.status.textContent = window.LCS?.t(`360 graden frame ${this.frameIndex + 1} van ${this.frames.length}`) || `360 graden frame ${this.frameIndex + 1} van ${this.frames.length}`;
        }
      }

      showSupport(mediaId) {
        this.cancelMotion();
        this.stopTeaser();
        this.stop();
        if (this.stage) this.stage.hidden = true;
        if (this.controls) this.controls.hidden = true;
        this.supportStages?.forEach((stage) => {
          stage.hidden = stage.dataset.spinSupport !== String(mediaId);
        });
        this.querySelectorAll('.shopify-spin-carousel__item').forEach((thumbnail) => {
          const isActive = thumbnail.dataset.spinSupportThumbnail === String(mediaId);
          thumbnail.classList.toggle('is-active', isActive);
          if (isActive) thumbnail.setAttribute('aria-current', 'true');
          else thumbnail.removeAttribute('aria-current');
        });
        if (this.status) this.status.textContent = window.LCS?.t('Ondersteunende productfoto geopend') || 'Ondersteunende productfoto geopend';
      }

      updateVariantMedia(variantId) {
        const mediaId = this.mediaMap?.[variantId];

        if (this.hiddenVariantMediaId) {
          this.gallery?.querySelector(`[data-media-id$="-${this.hiddenVariantMediaId}"]`)?.removeAttribute('hidden');
          const previousThumbnail = this.gallery?.querySelector(`[data-spin-media-id="${this.hiddenVariantMediaId}"]`);
          previousThumbnail?.setAttribute('data-target', `${this.slide.dataset.mediaId.replace('spin-360', '')}${this.hiddenVariantMediaId}`);
          previousThumbnail?.querySelector('button')?.removeAttribute('aria-current');
        }

        this.hiddenVariantMediaId = this.frames.length > 1 ? mediaId : null;
        if (mediaId && this.frames.length > 1) {
          const staticSlide = this.gallery?.querySelector(`[data-media-id$="-${mediaId}"]`);
          const staticThumbnail = this.gallery?.querySelector(`[data-spin-media-id="${mediaId}"]`);
          if (staticSlide && staticSlide !== this.slide) staticSlide.hidden = true;
          staticThumbnail?.setAttribute('data-target', this.slide.dataset.mediaId);
          staticThumbnail?.querySelector('button')?.setAttribute('aria-current', 'true');
        }

        window.setTimeout(() => {
          if (!this.frames.length || !this.slide?.dataset.mediaId) return;
          this.gallery?.querySelectorAll('[data-media-id].is-active').forEach((slide) => slide.classList.remove('is-active'));
          const mediaList = this.slide.parentElement;
          if (mediaList) {
            [...mediaList.querySelectorAll(':scope > [data-spin-static-order]')]
              .sort((left, right) => Number(left.dataset.spinStaticOrder) - Number(right.dataset.spinStaticOrder))
              .forEach((media) => mediaList.append(media));
          }
          this.slide.classList.add('is-active');

          const thumbnailList = this.gallery?.querySelector('.shopify-spin-thumbnail-list');
          if (thumbnailList) {
            [...thumbnailList.children]
              .sort((left, right) => Number(left.dataset.mediaPosition) - Number(right.dataset.mediaPosition))
              .forEach((thumbnail) => thumbnailList.append(thumbnail));
          }
        }, 0);
      }

      scheduleTeaser(force = false) {
        if ((!force && this.teaserShown) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.stopTeaser();
        this.teaserShown = true;
        const runId = this.teaserRunId;
        const frames = this.frames;
        if (frames.length < 2) return;
        const active = () => runId === this.teaserRunId && this.isConnected && this.frames === frames;
        const source = (index) => frames[index % frames.length].teaser || frames[index % frames.length].src;
        // Warm only the next two low-resolution frames, not the entire series.
        const firstReady = this.preloadSource(source(1));
        this.preloadSource(source(2));
        this.teaserDelayTimer = window.setTimeout(async () => {
          if (!(await firstReady) || !active()) return;
          this.teaserStep = 0;
          const advance = async () => {
            if (!active()) return;
            const next = this.teaserStep + 1;
            // Never flash a not-yet-loaded frame. Slow or failed loads retain
            // the current photo; cancellation cannot revive an old variant.
            const ready = await this.preloadSource(source(next));
            if (!active()) return;
            if (!ready) { this.stopTeaser(); this.showFrame(0); return; }
            this.showFrame(next, true);
            this.teaserStep += 1;
            if (this.teaserStep >= frames.length) {
              this.teaserDelayTimer = null;
              this.showFrame(0);
              return;
            }
            for (let ahead = 1; ahead <= 3; ahead += 1) this.preloadSource(source(next + ahead));
            this.teaserDelayTimer = window.setTimeout(advance, 110);
          };
          advance();
        }, 150);
      }

      stopTeaser() {
        this.teaserRunId += 1;
        if (this.teaserDelayTimer) window.clearTimeout(this.teaserDelayTimer);
        if (this.teaserInterval) window.clearInterval(this.teaserInterval);
        this.teaserDelayTimer = null;
        this.teaserInterval = null;
      }

      showFrame(index, useTeaserSource = false) {
        if (!this.frames.length || !this.image) return;
        this.frameIndex = (index + this.frames.length) % this.frames.length;
        const frame = this.frames[this.frameIndex];
        this.image.src = useTeaserSource ? frame.teaser || frame.src : frame.src;
        this.image.alt = frame.alt || '';
        if (this.status) this.status.textContent = window.LCS?.t(`360 graden frame ${this.frameIndex + 1} van ${this.frames.length}`) || `360 graden frame ${this.frameIndex + 1} van ${this.frames.length}`;
        // Teaser uses its own small-frame buffer. Do not download a second,
        // full-resolution series merely because the automatic preview plays.
        if (!useTeaserSource) {
          this.preloadFrame(this.frameIndex + 1);
          this.preloadFrame(this.frameIndex - 1);
        }
      }

      preloadFrame(index) {
        if (!this.frames.length) return;
        const normalizedIndex = (index + this.frames.length) % this.frames.length;
        const src = this.frames[normalizedIndex]?.src;
        if (!src || this.preloaded.has(src)) return;
        this.preloaded.add(src);
        const image = new Image();
        image.src = src;
      }

      preloadSource(src) {
        if (!src) return Promise.resolve(false);
        if (this.decodedSources.has(src)) return Promise.resolve(true);
        if (this.sourceLoads.has(src)) return this.sourceLoads.get(src);
        const isMotionSource = [...this.frames, ...(this.prewarmFrames || [])].some(frame => (frame.teaser || frame.src) === src);
        const promise = new Promise((resolve) => {
          const image = new Image();
          let settled = false;
          const finish = (ok) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeout);
            image.onload = image.onerror = null;
            resolve(ok);
          };
          const timeout = window.setTimeout(() => finish(false), 8000);
          image.fetchPriority = 'low';
          const decoded = async () => {
            try {
              if (image.decode) await image.decode();
              if (settled) return;
              if (image.naturalWidth > 0 && isMotionSource) {
                this.decodedSources.set(src, image);
                this.trimMotionCache();
              }
              finish(image.naturalWidth > 0);
            } catch (error) { finish(false); }
          };
          image.onload = decoded;
          image.onerror = () => finish(false);
          image.src = src;
          if (image.complete) decoded();
        });
        this.sourceLoads.set(src, promise);
        // Bound non-bitmap promise entries as well (sharp release frames).
        if (this.sourceLoads.size > 144) {
          for (const key of this.sourceLoads.keys()) {
            if (!this.decodedSources.has(key) && key !== src) this.sourceLoads.delete(key);
            if (this.sourceLoads.size <= 144) break;
          }
        }
        promise.then((ok) => { if (!ok && this.sourceLoads.get(src) === promise) this.sourceLoads.delete(src); });
        return promise;
      }

      trimMotionCache() {
        const active = new Set(this.frames.map(frame => frame.teaser || frame.src));
        for (const src of this.decodedSources.keys()) {
          if (this.decodedSources.size <= 72) break;
          if (!active.has(src)) { this.decodedSources.delete(src); this.sourceLoads.delete(src); }
        }
      }

      prepareMotionIntent(target) {
        const thumbnail = target.closest?.('[data-spin-variant-thumbnail]');
        let id = thumbnail?.dataset.spinVariantThumbnail;
        if (!id) {
          const picker = target.closest?.('variant-selects');
          if (!picker) return;
          const label = target.closest('label');
          const input = label?.control || target.closest('input,select');
          if (!input) return;
          const controls = [...picker.querySelectorAll('fieldset, select')];
          const index = controls.indexOf(input.closest('fieldset') || input);
          const values = [...(this.optionMap[this.currentVariantId] || [])];
          if (index < 0 || !values.length) return;
          values[index] = input.value;
          id = Object.keys(this.optionMap).find(key => this.optionMap[key].every((value, i) => value === values[i]));
        }
        const frames = this.spinMap[id];
        if (!frames || frames.length < 2 || frames[0].src === this.frames[0]?.src || frames === this.prewarmFrames) return;
        this.prewarmFrames = frames;
        this.warmMotionFrames(frames, true);
      }

      warmMotionFrames(frames = this.frames, preview = false) {
        if (frames.length < 2) return;
        // Three low-priority workers per active or anticipated color. Decoding is complete
        // before a frame is considered usable, so dragging needs no network.
        let cursor = 0;
        const worker = async () => {
          while (this.isConnected && (preview ? this.prewarmFrames === frames : this.frames === frames) && cursor < frames.length) {
            const frame = frames[cursor++];
            await this.preloadSource(frame.teaser || frame.src);
          }
        };
        worker(); worker(); worker();
      }

      cancelMotion() {
        this.motionRunId += 1;
        if (this.motionRaf != null) window.cancelAnimationFrame(this.motionRaf);
        this.motionRaf = null;
        this.motionTarget = null;
        this.dragStartX = null;
      }

      showMotionFrame(index) {
        if (!this.frames.length) return;
        const target = (index % this.frames.length + this.frames.length) % this.frames.length;
        this.motionTarget = target;
        const frame = this.frames[target];
        const src = frame.teaser || frame.src;
        const run = this.motionRunId;
        const draw = () => {
          if (run !== this.motionRunId || this.motionTarget !== target || !this.isConnected) return;
          if (this.frameIndex !== target || this.image.getAttribute('src') !== src) this.showFrame(target, true);
        };
        if (this.decodedSources.has(src)) draw();
        else this.preloadSource(src).then(ok => { if (ok) draw(); });
      }

      async refineMotionFrame(index) {
        const frames = this.frames;
        if (!frames.length) return;
        const target = (index % frames.length + frames.length) % frames.length;
        const run = this.motionRunId;
        // Preserve the currently visible small frame until the sharp one is
        // decoded. Never let an old release overwrite a new drag or variant.
        const ready = await this.preloadSource(frames[target].src);
        if (!ready || run !== this.motionRunId || this.frames !== frames || this.dragStartX !== null || !this.isConnected) return;
        this.motionTarget = null;
        this.showFrame(target);
      }

      step(amount) {
        this.cancelMotion();
        this.stopTeaser();
        this.stop();
        this.classList.add('shopify-spin--interacted');
        this.showFrame(this.frameIndex + amount);
      }

      togglePlay() {
        this.cancelMotion();
        this.stopTeaser();
        if (this.playTimer) {
          this.stop();
          return;
        }

        this.classList.add('shopify-spin--interacted');
        this.playButton?.setAttribute('aria-label', window.LCS?.t('Automatisch draaien pauzeren') || 'Automatisch draaien pauzeren');
        this.playTimer = window.setInterval(() => this.showFrame(this.frameIndex + 1), 90);
      }

      stop() {
        if (this.playTimer) window.clearInterval(this.playTimer);
        this.playTimer = null;
        this.playButton?.setAttribute('aria-label', window.LCS?.t('Automatisch draaien') || 'Automatisch draaien');
      }

      onPointerDown(event) {
        if (!this.frames.length) return;
        this.cancelMotion();
        this.stopTeaser();
        this.stop();
        this.classList.add('shopify-spin--interacted');
        this.dragStartX = event.clientX;
        this.dragStartFrame = this.frameIndex;
        this.dragPixelsPerFrame = Math.max(8, this.stage.clientWidth / this.frames.length / 2);
        this.motionTarget = this.frameIndex;
        this.stage.setPointerCapture?.(event.pointerId);
      }

      onPointerMove(event) {
        if (this.dragStartX === null) return;
        const pixelsPerFrame = this.dragPixelsPerFrame;
        const frameDelta = Math.round((this.dragStartX - event.clientX) / pixelsPerFrame);
        this.pendingDragFrame = this.dragStartFrame + frameDelta;
        if (this.motionRaf !== null) return;
        this.motionRaf = window.requestAnimationFrame(() => {
          this.motionRaf = null;
          if (this.dragStartX !== null) this.showMotionFrame(this.pendingDragFrame);
        });
      }

      onPointerUp(event) {
        if (this.dragStartX === null) return;
        if (this.motionRaf !== null) {
          window.cancelAnimationFrame(this.motionRaf);
          this.motionRaf = null;
          this.showMotionFrame(this.pendingDragFrame);
        }
        const target = this.motionTarget ?? this.frameIndex;
        this.dragStartX = null;
        this.stage.releasePointerCapture?.(event.pointerId);
        this.refineMotionFrame(target);
      }

      onKeyDown(event) {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }
    }
  );
}

if (!window.shopifySpinVariantWatcher) {
  window.shopifySpinVariantWatcher = window.setInterval(() => {
    const variantId = new URL(window.location.href).searchParams.get('variant');
    if (!variantId) return;
    document.querySelectorAll('shopify-spin-viewer').forEach((viewer) => {
      customElements.upgrade(viewer);
      if (viewer.currentVariantId !== variantId && typeof viewer.setVariant === 'function') viewer.setVariant(variantId);
    });
  }, 300);
}
